import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { User, Briefcase, GraduationCap, Code, Star, FileText, ChevronDown, ChevronUp, Plus, Trash2, Download, Zap, Sparkles, Rocket, Trophy, Award, Heart } from 'lucide-react';
import { validateFileSize, getErrorMessage, apiCall } from './utils/api';

// --- ⬇️ **IMPROVED useScript HOOK** ⬇️ ---
const useScript = (url) => {
    const [status, setStatus] = useState(url ? 'loading' : 'idle');

    useEffect(() => {
        if (!url) {
            setStatus('idle');
            return;
        }

        let script = document.querySelector(`script[src="${url}"]`);

        const setState = (event) => {
            const newStatus = event.type === 'load' ? 'ready' : 'error';
            if (script) {
                script.setAttribute('data-status', newStatus);
            }
            setStatus(newStatus);
        };

        if (!script) {
            script = document.createElement('script');
            script.src = url;
            script.async = true;
            script.setAttribute('data-status', 'loading');
            document.body.appendChild(script);
            script.addEventListener('load', setState);
            script.addEventListener('error', setState);
        } else {
            const currentStatus = script.getAttribute('data-status');
            if (currentStatus === 'ready' || currentStatus === 'error') {
                setStatus(currentStatus);
            } else {
                script.addEventListener('load', setState);
                script.addEventListener('error', setState);
                
                let loaded = false;
                if (url.includes('jspdf') && window.jspdf) loaded = true;
                if (url.includes('html2canvas') && window.html2canvas) loaded = true;
                
                if (loaded) {
                    setState({ type: 'load' });
                }
            }
        }

        return () => {
            if (script) {
                script.removeEventListener('load', setState);
                script.removeEventListener('error', setState);
            }
        };
    }, [url]);

    return status;
};

// --- ⬇️ **HELPER FUNCTION** ⬇️ ---
function downloadBlob(content, filename, mime = "text/plain;charset=utf-8") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// --- ⬇️ **SAFE RENDER HELPER** ⬇️ ---
// This prevents "Objects are not valid as a React child" errors
const safeRender = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return ''; // Ignore objects/arrays if they slip into text fields
    return String(value);
};

const mockSkills = ["JavaScript", "React", "Node.js", "Python", "Data Analysis", "Project Management", "UI/UX Design", "Tailwind CSS", "SQL", "Cloud Computing"];

const ResumePreview = forwardRef(({ data }, ref) => {
    if (!data) return null;

    // Safely handle skills array
    const skills = Array.isArray(data.skills)
        ? data.skills.filter(Boolean).map(String)
        : (typeof data.skills === 'string'
            ? data.skills.split(',').map(s => s.trim()).filter(Boolean)
            : []);

    return (
        <div ref={ref} className="bg-white text-gray-800 font-serif text-sm p-8">
            <header className="text-center mb-8">
                <h1 className="text-4xl font-bold tracking-wider">{safeRender(data.personalInfo?.name)}</h1>
                <p className="text-sm mt-2">
                    {safeRender(data.personalInfo?.email)} | {safeRender(data.personalInfo?.phone)} | {safeRender(data.personalInfo?.location)}
                </p>
            </header>

            {data.goal && (
                <section className="mb-6">
                    <h2 className="text-xl font-bold border-b-2 border-gray-300 pb-1 mb-2">Career Goal</h2>
                    <p>{safeRender(data.goal)}</p>
                </section>
            )}

            <section className="mb-6">
                <h2 className="text-xl font-bold border-b-2 border-gray-300 pb-1 mb-2">Work Experience</h2>
                {(data.experience || []).map(exp => (
                    <div key={exp.id || Math.random()} className="mb-4">
                        <h3 className="font-bold">{safeRender(exp.title)} - {safeRender(exp.company)}</h3>
                        <p className="text-sm text-gray-600">{safeRender(exp.period)}</p>
                        <p className="mt-1 whitespace-pre-wrap">{safeRender(exp.description)}</p>
                    </div>
                ))}
            </section>

            <section className="mb-6">
                <h2 className="text-xl font-bold border-b-2 border-gray-300 pb-1 mb-2">Education</h2>
                {(data.education || []).map(edu => (
                    <div key={edu.id || Math.random()} className="mb-4">
                        <h3 className="font-bold">{safeRender(edu.degree)} - {safeRender(edu.school)}</h3>
                        <p className="text-sm text-gray-600">{safeRender(edu.period)}</p>
                        <p className="mt-1 whitespace-pre-wrap">{safeRender(edu.description)}</p>
                    </div>
                ))}
            </section>

            <section className="mb-6">
                <h2 className="text-xl font-bold border-b-2 border-gray-300 pb-1 mb-2">Projects</h2>
                {(data.projects || []).map(proj => (
                    <div key={proj.id || Math.random()} className="mb-4">
                        <h3 className="font-bold">{safeRender(proj.title)}</h3>
                        <p className="text-sm text-gray-600">{safeRender(proj.period)}</p>
                        <p className="mt-1 whitespace-pre-wrap">{safeRender(proj.description)}</p>
                    </div>
                ))}
            </section>

            <section className="mb-6">
                <h2 className="text-xl font-bold border-b-2 border-gray-300 pb-1 mb-2">Skills</h2>
                <p>{skills.join(', ')}</p>
            </section>

            {(data.achievements && data.achievements.length > 0) && (
                <section className="mb-6">
                    <h2 className="text-xl font-bold border-b-2 border-gray-300 pb-1 mb-2">Achievements</h2>
                    <ul className="list-disc list-inside">
                        {(data.achievements || []).map(item => (
                            <li key={item.id || Math.random()} className="whitespace-pre-wrap">{safeRender(item.description)}</li>
                        ))}
                    </ul>
                </section>
            )}

            {(data.awards && data.awards.length > 0) && (
                <section className="mb-6">
                    <h2 className="text-xl font-bold border-b-2 border-gray-300 pb-1 mb-2">Awards</h2>
                    <ul className="list-disc list-inside">
                        {(data.awards || []).map(item => (
                            <li key={item.id || Math.random()} className="whitespace-pre-wrap">{safeRender(item.description)}</li>
                        ))}
                    </ul>
                </section>
            )}

            {(data.certificates && data.certificates.length > 0) && (
                <section className="mb-6">
                    <h2 className="text-xl font-bold border-b-2 border-gray-300 pb-1 mb-2">Certificates</h2>
                    <ul className="list-disc list-inside">
                        {(data.certificates || []).map(item => (
                            <li key={item.id || Math.random()} className="whitespace-pre-wrap">{safeRender(item.description)}</li>
                        ))}
                    </ul>
                </section>
            )}

            {(data.hobbies && data.hobbies.length > 0) && (
                <section> 
                    <h2 className="text-xl font-bold border-b-2 border-gray-300 pb-1 mb-2">Hobbies</h2>
                    <ul className="list-disc list-inside">
                        {(data.hobbies || []).map(item => (
                            <li key={item.id || Math.random()} className="whitespace-pre-wrap">{safeRender(item.description)}</li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
});


const ResumeBuilder = ({ data, onDataChange, onAnalyze, onParseComplete }) => {
    const [activeSection, setActiveSection] = useState('personalInfo');
    const [isExporting, setIsExporting] = useState(false);
    const resumePreviewRef = useRef();
    const [builderMode, setBuilderMode] = useState('manual'); 
    const [uploadedFile, setUploadedFile] = useState(null);
    const [isParsing, setIsParsing] = useState(false);
    const [error, setError] = useState(null);

    const getSkillsArray = () => {
        const value = data?.skills;
        if (Array.isArray(value)) return value.filter(Boolean).map(String);
        if (typeof value === 'string') {
            return value.split(',').map(s => s.trim()).filter(Boolean);
        }
        if (value && typeof value === 'object') {
            try {
                const maybeIterable = Array.from(value);
                return maybeIterable.filter(Boolean).map(String);
            } catch(err) {
                console.error('Error converting skills object to array:', err);
                return [];
            }
        }
        return [];
    };

    const jspdfStatus = useScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
    const html2canvasStatus = useScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js');
    const scriptsReady = jspdfStatus === 'ready' && html2canvasStatus === 'ready';

    const handleAddItem = (section) => {
        const newItem = { id: Date.now(), title: '', company: '', period: '', description: '' };
        if (section === 'education') {
            Object.assign(newItem, { school: '', degree: '' });
        }
        if (['achievements', 'awards', 'certificates', 'hobbies'].includes(section)) {
            Object.assign(newItem, { title: undefined, company: undefined, period: undefined });
        }
        onDataChange(section, [...(data[section] || []), newItem]);
    };

    const handleRemoveItem = (section, id) => {
        onDataChange(section, data[section].filter(item => item.id !== id));
    };

    const handleItemChange = (section, id, field, value) => {
        const updatedItems = data[section].map(item =>
            item.id === id ? { ...item, [field]: value } : item
        );
        onDataChange(section, updatedItems);
    };

    const handleSkillsChange = (skill) => {
        const currentSkills = getSkillsArray();
        const newSkills = currentSkills.includes(skill)
            ? currentSkills.filter(s => s !== skill)
            : [...currentSkills, skill];
        onDataChange('skills', newSkills);
    };

    const handleExport = async (format) => {
        if (format === 'pdf' && !scriptsReady) {
            setError("PDF export scripts are not ready yet.");
            return;
        }
        
        setIsExporting(true);
        setError(null);

        if (format === 'pdf') {
            const resumeElement = resumePreviewRef.current;
            if (!resumeElement) {
                setError("Resume preview element not found.");
                setIsExporting(false);
                return;
            }

            await document.fonts.ready;

            const clone = resumeElement.cloneNode(true);
            clone.classList.remove('p-8'); 
            clone.style.padding = '1in'; 
            clone.style.width = '8.5in'; 
            clone.style.minHeight = '11in'; 
            clone.style.boxSizing = 'border-box';
            clone.style.margin = '0';
            clone.style.background = 'white';
            clone.style.fontFamily = 'Times, "Times New Roman", serif'; 
            clone.style.fontSize = '12pt'; 
            clone.querySelectorAll('.text-4xl').forEach(el => { el.style.fontSize = '24pt'; el.style.fontWeight = 'bold'; });
            clone.querySelectorAll('.text-xl').forEach(el => { el.style.fontSize = '16pt'; el.style.fontWeight = 'bold'; });
            clone.querySelectorAll('.text-sm').forEach(el => { el.style.fontSize = '10pt'; });
            clone.querySelectorAll('.font-bold').forEach(el => { el.style.fontWeight = 'bold'; });
            clone.querySelectorAll('.text-gray-600').forEach(el => { el.style.color = '#555'; });
            clone.querySelectorAll('.border-b-2').forEach(el => { el.style.borderBottom = '2px solid #ccc'; });
            clone.querySelectorAll('.pb-1').forEach(el => { el.style.paddingBottom = '0.25rem'; });
            clone.querySelectorAll('.mb-1').forEach(el => { el.style.marginBottom = '0.25rem'; });
            clone.querySelectorAll('.mb-2').forEach(el => { el.style.marginBottom = '0.5rem'; });
            clone.querySelectorAll('.mb-4').forEach(el => { el.style.marginBottom = '1rem'; });
            clone.querySelectorAll('.mb-6').forEach(el => { el.style.marginBottom = '1.5rem'; });
            clone.querySelectorAll('.mb-8').forEach(el => { el.style.marginBottom = '2rem'; });
            clone.querySelectorAll('.mt-1').forEach(el => { el.style.marginTop = '0.25rem'; });
            clone.querySelectorAll('.mt-2').forEach(el => { el.style.marginTop = '0.5rem'; });
            clone.querySelectorAll('.text-center').forEach(el => { el.style.textAlign = 'center'; });
            clone.querySelectorAll('.whitespace-pre-wrap').forEach(el => { el.style.whiteSpace = 'pre-wrap'; });
            clone.querySelectorAll('ul.list-disc').forEach(el => {
                el.style.listStyleType = 'disc';
                el.style.listStylePosition = 'inside';
                el.style.paddingLeft = '1.5rem'; 
            });
            clone.querySelectorAll('li').forEach(el => {
                el.style.marginBottom = '0.25rem'; 
            });

            const staging = document.createElement('div');
            staging.style.position = 'absolute';
            staging.style.left = '-9999px';
            staging.style.top = 'auto';
            staging.appendChild(clone);
            document.body.appendChild(staging);

            const safeName = (data?.personalInfo?.name || 'Resume').replace(/ /g, '_');

            try {
                const { jsPDF } = window.jspdf;
                const pdf = new jsPDF({ orientation: 'portrait', unit: 'in', format: 'letter' });

                await pdf.html(clone, {
                    callback: function (doc) {
                        doc.save(`${safeName}_Resume.pdf`);
                    },
                    x: 0,
                    y: 0,
                    width: 8.5, 
                    windowWidth: 8.5 * 96, 
                    autoPaging: 'text', 
                    margin: [0, 0, 0, 0] 
                });

            } catch (error) {
                console.error(`Error generating pdf:`, error);
                setError(`Failed to export PDF. Please try again.`);
            } finally {
                document.body.removeChild(staging);
                setIsExporting(false);
            }

        } else if (format === 'docx') {
            const safeName = (data?.personalInfo?.name || 'Resume').replace(/ /g, '_');
            const title = `${data?.personalInfo?.name || 'Resume'}`;
            
            const safeHtml = (plainText) => {
                // Defensive check for object values in DOCX export too
                if (typeof plainText === 'object') return '';
                return (plainText || "")
                    .replace(/&/g, "&amp;")
                    .replace(/</g, "&lt;")
                    .replace(/>/g, "&gt;")
                    .replace(/\n/g, "<br>");
            };
            
            const styles = {
                body: `font-family: 'Times New Roman', Times, serif; font-size: 12pt; line-height: 1.5;`,
                h1: `font-size: 24pt; text-align: center; margin-bottom: 5pt; font-weight: bold;`,
                h2: `font-size: 16pt; border-bottom: 2px solid #333; padding-bottom: 3pt; margin-top: 15pt; margin-bottom: 10pt; font-weight: bold;`,
                h3: `font-size: 12pt; font-weight: bold; margin-bottom: 3pt;`,
                p: `margin: 5pt 0;`,
                contact: `font-size: 10pt; text-align: center; margin-bottom: 15pt;`,
                item: `margin-bottom: 10pt;`,
                date: `color: #555; font-size: 10pt;`,
                li: `margin-bottom: 5pt;`
            };

            const personalInfo = data.personalInfo || {};
            const skills = getSkillsArray();
            
            let docHTML = `
            <!DOCTYPE html>
            <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">
            <head><meta charset="utf-8"><title>${title}</title></head>
            <body style="${styles.body}">
                <h1 style="${styles.h1}">${safeHtml(personalInfo.name) || 'Your Name'}</h1>
                <p style="${styles.contact}">${safeHtml(personalInfo.email)} | ${safeHtml(personalInfo.phone)} | ${safeHtml(personalInfo.location)}</p>
            `;
            
            if (data.goal && typeof data.goal !== 'object') {
                docHTML += `<div><h2 style="${styles.h2}">Career Goal</h2><p style="${styles.p}">${safeHtml(data.goal)}</p></div>`;
            }
            
            if (data.experience && data.experience.length > 0) {
                docHTML += `<div><h2 style="${styles.h2}">Work Experience</h2>`;
                data.experience.forEach(exp => {
                    docHTML += `
                    <div style="${styles.item}">
                        <h3 style="${styles.h3}">${safeHtml(exp.title)} - ${safeHtml(exp.company)}</h3>
                        <p style="${styles.date}">${safeHtml(exp.period)}</p>
                        <p style="${styles.p}">${safeHtml(exp.description)}</p>
                    </div>
                    `;
                });
                docHTML += `</div>`;
            }
            
            if (data.education && data.education.length > 0) {
                docHTML += `<div><h2 style="${styles.h2}">Education</h2>`;
                data.education.forEach(edu => {
                    docHTML += `
                    <div style="${styles.item}">
                        <h3 style="${styles.h3}">${safeHtml(edu.degree)} - ${safeHtml(edu.school)}</h3>
                        <p style="${styles.date}">${safeHtml(edu.period)}</p>
                        ${edu.description ? `<p style="${styles.p}">${safeHtml(edu.description)}</p>` : ''}
                    </div>
                    `;
                });
                docHTML += `</div>`;
            }
            
            if (data.projects && data.projects.length > 0) {
                docHTML += `<div><h2 style="${styles.h2}">Projects</h2>`;
                data.projects.forEach(proj => {
                    docHTML += `
                    <div style="${styles.item}">
                        <h3 style="${styles.h3}">${safeHtml(proj.title)}</h3>
                        <p style="${styles.date}">${safeHtml(proj.period)}</p>
                        <p style="${styles.p}">${safeHtml(proj.description)}</p>
                    </div>
                    `;
                });
                docHTML += `</div>`;
            }
            
            if (skills.length > 0) {
                docHTML += `<div><h2 style="${styles.h2}">Skills</h2><p style="${styles.p}">${safeHtml(skills.join(', '))}</p></div>`;
            }
            
            if (data.achievements && data.achievements.length > 0) {
                docHTML += `<div><h2 style="${styles.h2}">Achievements</h2><ul>`;
                data.achievements.forEach(item => {
                    docHTML += `<li style="${styles.li}">${safeHtml(item.description)}</li>`;
                });
                docHTML += `</ul></div>`;
            }
            
            if (data.awards && data.awards.length > 0) {
                docHTML += `<div><h2 style="${styles.h2}">Awards</h2><ul>`;
                data.awards.forEach(item => {
                    docHTML += `<li style="${styles.li}">${safeHtml(item.description)}</li>`;
                });
                docHTML += `</ul></div>`;
            }
            
            if (data.certificates && data.certificates.length > 0) {
                docHTML += `<div><h2 style="${styles.h2}">Certificates</h2><ul>`;
                data.certificates.forEach(item => {
                    docHTML += `<li style="${styles.li}">${safeHtml(item.description)}</li>`;
                });
                docHTML += `</ul></div>`;
            }
            
            if (data.hobbies && data.hobbies.length > 0) {
                docHTML += `<div><h2 style="${styles.h2}">Hobbies</h2><ul>`;
                data.hobbies.forEach(item => {
                    docHTML += `<li style="${styles.li}">${safeHtml(item.description)}</li>`;
                });
                docHTML += `</ul></div>`;
            }
            
            docHTML += `</body></html>`;

            try {
                downloadBlob(docHTML, `${safeName}_Resume.doc`, "application/msword;charset=utf-8");
            } catch (docxErr) {
                console.error("Error creating DOC:", docxErr);
                setError(`Failed to export DOC file.`);
            } finally {
                setIsExporting(false);
            }
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            try {
                validateFileSize(file, 10);
                setUploadedFile(file);
                setError(null); 
            } catch (err) {
                setError(getErrorMessage(err));
                setUploadedFile(null);
                e.target.value = ''; 
            }
        }
    };

    const handleParseResume = async () => {
        if (!uploadedFile) {
            setError("Please select a file to parse.");
            return;
        }
        setIsParsing(true);
        setError(null);

        const formData = new FormData();
        formData.append('resume_file', uploadedFile);

        try {
            const apiResp = await apiCall('/api/parse-resume', {
                method: 'POST',
                body: formData,
            });

            const payload = apiResp?.data ?? apiResp;
            const parsedData = payload?.normalized ?? payload ?? {};

            // Helper to sanitize incoming text fields to ensure no Objects slip in
            const sanitizeText = (val) => (typeof val === 'object' && val !== null) ? '' : val;

            // Normalize and Sanitize the data
            const normalized = {
                personalInfo: { 
                    name: sanitizeText(parsedData.personalInfo?.name) || "", 
                    email: sanitizeText(parsedData.personalInfo?.email) || "", 
                    phone: sanitizeText(parsedData.personalInfo?.phone) || "", 
                    location: sanitizeText(parsedData.personalInfo?.location) || "" 
                },
                goal: sanitizeText(parsedData.goal) || "",
                experience: (parsedData.experience || []).map(item => ({
                    ...item,
                    title: sanitizeText(item.title || item.position), // Fallback if parsed title is empty
                    company: sanitizeText(item.company),
                    period: sanitizeText(item.period || item.startDate ? `${item.startDate} - ${item.endDate}` : ''),
                    description: sanitizeText(item.description)
                })),
                education: (parsedData.education || []).map(item => ({
                    ...item,
                    school: sanitizeText(item.school || item.institution),
                    degree: sanitizeText(item.degree),
                    period: sanitizeText(item.period || item.startDate ? `${item.startDate} - ${item.endDate}` : ''),
                    description: sanitizeText(item.description)
                })),
                projects: (parsedData.projects || []).map(item => ({
                    ...item,
                    title: sanitizeText(item.title),
                    period: sanitizeText(item.period),
                    description: sanitizeText(item.description)
                })),
                skills: parsedData.skills || [],
                achievements: (parsedData.achievements || []).map(item => ({ ...item, description: sanitizeText(item.description) })),
                awards: (parsedData.awards || []).map(item => ({ ...item, description: sanitizeText(item.description) })),
                certificates: (parsedData.certificates || []).map(item => ({ ...item, description: sanitizeText(item.description) })),
                hobbies: (parsedData.hobbies || []).map(item => ({ ...item, description: sanitizeText(item.description) })),
            };

            await onParseComplete(normalized);
            setBuilderMode('manual');
        } catch (err) {
            console.error("Error parsing resume:", err);
            setError(err?.message || String(err));
        } finally {
            setIsParsing(false);
            setUploadedFile(null);
        }
    };

    return (
        <div>
            {/* The hidden preview is still used as the *source* for the PDF export clone */}
            <div className="hidden">
                <ResumePreview ref={resumePreviewRef} data={data} />
            </div>
            
            <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                <div>
                    <h2 className="text-3xl font-bold text-white">AI Resume Builder</h2>
                    <p className="text-gray-400 mt-1">Your data is saved automatically.</p>
                </div>
                <div className="flex items-center space-x-2">
                    <div className="relative group">
                        <button disabled={isExporting} className="bg-gray-800 text-white border border-gray-600 font-semibold py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed">
                            <Download size={18} />
                            <span>{isExporting ? 'Exporting...' : 'Export'}</span>
                            <ChevronDown size={16} />
                        </button>
                        
                        <div className="absolute right-0 mt-2 w-40 bg-gray-800 border border-gray-700 rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10">
                            <div
                                onClick={() => !isExporting && handleExport('pdf')}
                                className={`block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 cursor-pointer ${!scriptsReady ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {scriptsReady ? 'Export as PDF' : 'Loading PDF...'}
                            </div>
                            <div
                                onClick={() => !isExporting && handleExport('docx')}
                                className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 cursor-pointer"
                            >
                                Export as Word
                            </div>
                        </div>
                    </div>
                    <button onClick={onAnalyze} className="bg-[#FFD600] text-[#0E0E0E] font-semibold py-2 px-6 rounded-lg hover:bg-[#FFD600]/90 transition-all duration-300 ease-in-out shadow-md hover:shadow-lg transform hover:-translate-y-px flex items-center space-x-2">
                        <Zap size={18} />
                        <span>Analyze</span>
                    </button>
                </div>
            </header>

            {error && (
                <div className="mb-4 p-4 bg-red-900/20 border border-red-500 rounded-lg text-red-300">
                    {error}
                </div>
            )}

            <div className="mb-4 border-b border-gray-700">
                <nav className="-mb-px flex space-x-6">
                    <button onClick={() => setBuilderMode('manual')} className={`py-3 px-1 border-b-2 font-bold text-sm ${builderMode === 'manual' ? 'border-[#FFD600] text-[#FFD600]' : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600'}`}>
                        AI Builder
                    </button>
                    <button onClick={() => setBuilderMode('upload')} className={`py-3 px-1 border-b-2 font-semibold text-sm ${builderMode === 'upload' ? 'border-[#FFD600] text-[#FFD600]' : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600'}`}>
                        Upload Existing Resume
                    </button>
                </nav>
            </div>

            {builderMode === 'manual' ? (
                <div className="space-y-6">
                    <AccordionSection title="Personal Information" icon={<User size={20} />} isOpen={activeSection === 'personalInfo'} onToggle={() => setActiveSection(activeSection === 'personalInfo' ? null : 'personalInfo')}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Input label="Full Name" value={data.personalInfo.name} onChange={e => onDataChange('personalInfo', { ...data.personalInfo, name: e.target.value })} />
                            <Input label="Email" value={data.personalInfo.email} onChange={e => onDataChange('personalInfo', { ...data.personalInfo, email: e.target.value })} />
                            <Input label="Phone" value={data.personalInfo.phone} onChange={e => onDataChange('personalInfo', { ...data.personalInfo, phone: e.target.value })} />
                            <Input label="Location" value={data.personalInfo.location} onChange={e => onDataChange('personalInfo', { ...data.personalInfo, location: e.target.value })} />
                        </div>
                    </AccordionSection>

                    <AccordionSection title="Goal" icon={<Rocket size={20} />} isOpen={activeSection === 'goal'} onToggle={() => setActiveSection(activeSection === 'goal' ? null : 'goal')}>
                        <TextArea label="Your Career Goal" value={data.goal} onChange={e => onDataChange('goal', e.target.value)} />
                    </AccordionSection>

                    <AccordionSection title="Work Experience" icon={<Briefcase size={20} />} isOpen={activeSection === 'experience'} onToggle={() => setActiveSection(activeSection === 'experience' ? null : 'experience')}>
                        {(data.experience || []).map((exp) => (
                            <div key={exp.id} className="p-4 bg-gray-800/50 rounded-lg mb-4 relative">
                                <button onClick={() => handleRemoveItem('experience', exp.id)} className="absolute top-2 right-2 text-gray-500 hover:text-red-500"><Trash2 size={16} /></button>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Input label="Job Title" value={exp.title} onChange={e => handleItemChange('experience', exp.id, 'title', e.target.value)} />
                                    <Input label="Company" value={exp.company} onChange={e => handleItemChange('experience', exp.id, 'company', e.target.value)} />
                                </div>
                                <Input label="Period (e.g., Jan 2022 - Present)" value={exp.period} onChange={e => handleItemChange('experience', exp.id, 'period', e.target.value)} />
                                <TextArea label="Description" value={exp.description} onChange={e => handleItemChange('experience', exp.id, 'description', e.target.value)} />
                            </div>
                        ))}
                        <button onClick={() => handleAddItem('experience')} className="text-[#FFD600] font-semibold text-sm flex items-center space-x-1 mt-2"><Plus size={16} /><span>Add Experience</span></button>
                    </AccordionSection>

                    <AccordionSection title="Education" icon={<GraduationCap size={20} />} isOpen={activeSection === 'education'} onToggle={() => setActiveSection(activeSection === 'education' ? null : 'education')}>
                        {(data.education || []).map((edu) => (
                            <div key={edu.id} className="p-4 bg-gray-800/50 rounded-lg mb-4 relative">
                                <button onClick={() => handleRemoveItem('education', edu.id)} className="absolute top-2 right-2 text-gray-500 hover:text-red-500"><Trash2 size={16} /></button>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Input label="School/University" value={edu.school} onChange={e => handleItemChange('education', edu.id, 'school', e.target.value)} />
                                    <Input label="Degree" value={edu.degree} onChange={e => handleItemChange('education', edu.id, 'degree', e.target.value)} />
                                </div>
                                <Input label="Period (e.g., 2018 - 2022)" value={edu.period} onChange={e => handleItemChange('education', edu.id, 'period', e.target.value)} />
                                <TextArea label="Description (Optional)" value={edu.description} onChange={e => handleItemChange('education', edu.id, 'description', e.target.value)} />
                            </div>
                        ))}
                        <button onClick={() => handleAddItem('education')} className="text-[#FFD600] font-semibold text-sm flex items-center space-x-1 mt-2"><Plus size={16} /><span>Add Education</span></button>
                    </AccordionSection>

                    <AccordionSection title="Projects" icon={<Code size={20} />} isOpen={activeSection === 'projects'} onToggle={() => setActiveSection(activeSection === 'projects' ? null : 'projects')}>
                        {(data.projects || []).map((proj) => (
                            <div key={proj.id} className="p-4 bg-gray-800/50 rounded-lg mb-4 relative">
                                <button onClick={() => handleRemoveItem('projects', proj.id)} className="absolute top-2 right-2 text-gray-500 hover:text-red-500"><Trash2 size={16} /></button>
                                <Input label="Project Title" value={proj.title} onChange={e => handleItemChange('projects', proj.id, 'title', e.target.value)} />
                                <Input label="Period (e.g., 2023)" value={proj.period} onChange={e => handleItemChange('projects', proj.id, 'period', e.target.value)} />
                                <TextArea label="Description" value={proj.description} onChange={e => handleItemChange('projects', proj.id, 'description', e.target.value)} />
                            </div>
                        ))}
                        <button onClick={() => handleAddItem('projects')} className="text-[#FFD600] font-semibold text-sm flex items-center space-x-1 mt-2"><Plus size={16} /><span>Add Project</span></button>
                    </AccordionSection>

                    <AccordionSection title="Skills" icon={<Star size={20} />} isOpen={activeSection === 'skills'} onToggle={() => setActiveSection(activeSection === 'skills' ? null : 'skills')}>
                        <div className="flex flex-wrap gap-2">
                            {mockSkills.map(skill => (
                                <button key={skill} onClick={() => handleSkillsChange(skill)} className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${getSkillsArray().includes(skill) ? 'bg-[#FFD600]/20 text-[#FFD600] border-[#FFD600]/30' : 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700'}`}>
                                    {skill}
                                </button>
                            ))}
                        </div>
                    </AccordionSection>

                    <AccordionSection title="Achievements" icon={<Trophy size={20} />} isOpen={activeSection === 'achievements'} onToggle={() => setActiveSection(activeSection === 'achievements' ? null : 'achievements')}>
                        {(data.achievements || []).map((item) => (
                            <div key={item.id} className="p-4 bg-gray-800/50 rounded-lg mb-4 relative">
                                <button onClick={() => handleRemoveItem('achievements', item.id)} className="absolute top-2 right-2 text-gray-500 hover:text-red-500"><Trash2 size={16} /></button>
                                <TextArea label="Achievement" value={item.description} onChange={e => handleItemChange('achievements', item.id, 'description', e.target.value)} />
                            </div>
                        ))}
                        <button onClick={() => handleAddItem('achievements')} className="text-[#FFD600] font-semibold text-sm flex items-center space-x-1 mt-2"><Plus size={16} /><span>Add Achievement</span></button>
                    </AccordionSection>

                    <AccordionSection title="Awards" icon={<Award size={20} />} isOpen={activeSection === 'awards'} onToggle={() => setActiveSection(activeSection === 'awards' ? null : 'awards')}>
                        {(data.awards || []).map((item) => (
                            <div key={item.id} className="p-4 bg-gray-800/50 rounded-lg mb-4 relative">
                                <button onClick={() => handleRemoveItem('awards', item.id)} className="absolute top-2 right-2 text-gray-500 hover:text-red-500"><Trash2 size={16} /></button>
                                <TextArea label="Award" value={item.description} onChange={e => handleItemChange('awards', item.id, 'description', e.target.value)} />
                            </div>
                        ))}
                        <button onClick={() => handleAddItem('awards')} className="text-[#FFD600] font-semibold text-sm flex items-center space-x-1 mt-2"><Plus size={16} /><span>Add Award</span></button>
                    </AccordionSection>

                    <AccordionSection title="Certificates" icon={<FileText size={20} />} isOpen={activeSection === 'certificates'} onToggle={() => setActiveSection(activeSection === 'certificates' ? null : 'certificates')}>
                        {(data.certificates || []).map((item) => (
                            <div key={item.id} className="p-4 bg-gray-800/50 rounded-lg mb-4 relative">
                                <button onClick={() => handleRemoveItem('certificates', item.id)} className="absolute top-2 right-2 text-gray-500 hover:text-red-500"><Trash2 size={16} /></button>
                                <TextArea label="Certificate" value={item.description} onChange={e => handleItemChange('certificates', item.id, 'description', e.target.value)} />
                            </div>
                        ))}
                        <button onClick={() => handleAddItem('certificates')} className="text-[#FFD600] font-semibold text-sm flex items-center space-x-1 mt-2"><Plus size={16} /><span>Add Certificate</span></button>
                    </AccordionSection>

                    <AccordionSection title="Hobbies" icon={<Heart size={20} />} isOpen={activeSection === 'hobbies'} onToggle={() => setActiveSection(activeSection === 'hobbies' ? null : 'hobbies')}>
                        {(data.hobbies || []).map((item) => (
                            <div key={item.id} className="p-4 bg-gray-800/50 rounded-lg mb-4 relative">
                                <button onClick={() => handleRemoveItem('hobbies', item.id)} className="absolute top-2 right-2 text-gray-500 hover:text-red-500"><Trash2 size={16} /></button>
                                <TextArea label="Hobby" value={item.description} onChange={e => handleItemChange('hobbies', item.id, 'description', e.target.value)} />
                            </div>
                        ))}
                        <button onClick={() => handleAddItem('hobbies')} className="text-[#FFD600] font-semibold text-sm flex items-center space-x-1 mt-2"><Plus size={16} /><span>Add Hobby</span></button>
                    </AccordionSection>
                </div>
            ) : (
                <div className="py-8">
                    <h3 className="text-xl font-bold text-center text-white">Upload Your Resume</h3>
                    <p className="text-gray-400 mt-2 mb-6 text-center max-w-md mx-auto">Upload a DOCX or PDF file and our AI will automatically populate the builder for you.</p>
                    <div className="max-w-lg mx-auto">
                        <label htmlFor="resume-upload" className="block text-sm font-medium text-gray-400 mb-2">Resume File</label>
                        <input id="resume-upload" type="file" onChange={handleFileChange} accept=".pdf,.doc,.docx" className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-800 file:text-gray-200 hover:file:bg-gray-700" />
                        <div className="text-center mt-6">
                            <button onClick={handleParseResume} disabled={!uploadedFile || isParsing} className="bg-[#FFD600] text-[#0E0E0E] font-semibold py-2 px-6 rounded-lg hover:bg-[#FFD600]/90 transition-colors flex items-center space-x-2 mx-auto disabled:bg-gray-500 disabled:cursor-not-allowed">
                                {isParsing ? (
                                    <svg className="animate-spin h-5 w-5 text-gray-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                ) : (
                                    <Sparkles size={18} />
                                )}
                                <span>{isParsing ? 'Parsing...' : 'Parse Resume'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const AccordionSection = ({ title, icon, isOpen, onToggle, children }) => (
    <div className="bg-gray-900 border border-gray-700 rounded-xl overflow-hidden shadow-sm">
        <button onClick={onToggle} className="w-full flex justify-between items-center p-4 text-left">
            <div className="flex items-center space-x-3">
                <div className="bg-gray-800 text-[#FFD600] p-2 rounded-full">{icon}</div>
                <h3 className="text-lg font-semibold text-white">{title}</h3>
            </div>
            {isOpen ? <ChevronUp size={20} className="text-gray-500" /> : <ChevronDown size={20} className="text-gray-500" />}
        </button>
        {isOpen && <div className="p-4 border-t border-gray-700">{children}</div>}
    </div>
);

const Input = ({ label, ...props }) => (
    <div className="space-y-2 w-full">
        {label && <label className="text-sm font-medium text-gray-400">{label}</label>}
        <input {...props} className="w-full px-4 py-3 bg-gray-800 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#FFD600] focus:border-[#FFD600] transition-shadow" />
    </div>
);

const TextArea = ({ label, ...props }) => (
    <div className="space-y-2">
        <label className="text-sm font-medium text-gray-400">{label}</label>
        <textarea {...props} rows="4" className="w-full px-4 py-3 bg-gray-800 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#FFD600] focus:border-[#FFD600] transition-shadow"></textarea>
    </div>
);

export default ResumeBuilder;